import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useState, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/** Props para el asa de arrastre (se ponen en el botón que el usuario agarra). */
export type AsaProps = HTMLAttributes<HTMLElement>;

function Fila({
  id,
  deshabilitado,
  className,
  children,
}: {
  id: number;
  deshabilitado: boolean;
  className?: string;
  children: (asa: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: deshabilitado,
  });
  const asa = deshabilitado ? null : (
    <button
      type="button"
      className="cursor-grab touch-none rounded p-1 text-texto-3 hover:text-texto active:cursor-grabbing"
      aria-label="Arrastrar para ordenar"
      {...attributes}
      {...listeners}
    >
      <GripVertical size={16} aria-hidden />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(className, isDragging && 'relative z-10 opacity-80 shadow-lg')}
    >
      {children(asa)}
    </div>
  );
}

/**
 * Lista o cuadrícula que se ordena arrastrando (ratón, toque o teclado). El orden nuevo se
 * entrega con `onOrdenar(ids)`; quien la usa lo guarda con su `PUT …/order`. Mientras se guarda,
 * se muestra el orden local para que no "brinque".
 */
export function OrdenableLista<T>({
  items,
  idDe,
  onOrdenar,
  children,
  cuadricula = false,
  deshabilitado = false,
  className,
  claseItem,
}: {
  items: T[];
  idDe: (item: T) => number;
  onOrdenar: (ids: number[]) => void;
  children: (item: T, asa: ReactNode, indice: number) => ReactNode;
  cuadricula?: boolean;
  deshabilitado?: boolean;
  className?: string;
  claseItem?: string;
}) {
  const [local, setLocal] = useState<number[] | null>(null);
  const [base, setBase] = useState(items);
  if (base !== items) {
    setBase(items);
    setLocal(null);
  }
  const porId = new Map(items.map((i) => [idDe(i), i]));
  const orden = (local ?? items.map(idDe)).filter((id) => porId.has(id));
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const alSoltar = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const nuevo = arrayMove(
      orden,
      orden.indexOf(Number(active.id)),
      orden.indexOf(Number(over.id)),
    );
    setLocal(nuevo);
    onOrdenar(nuevo);
  };

  return (
    <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
      <SortableContext
        items={orden}
        strategy={cuadricula ? rectSortingStrategy : verticalListSortingStrategy}
      >
        <div className={className}>
          {orden.map((id, i) => (
            <Fila key={id} id={id} deshabilitado={deshabilitado} className={claseItem}>
              {(asa) => children(porId.get(id) as T, asa, i)}
            </Fila>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
